// ** Custom Components
import React, { useEffect, useState } from "react"
import Box from "../Box"
import Container from "../Container"
import Heading from "../Heading"
import Text from "../Text"
// ** Utils
import { serializers } from "../Serializers"

// ** 3rd Party Library
import { PortableText } from "@portabletext/react"
import "./layoutCal.css"

const SectionAffordabilityCalculator = ({ section }: any) => {
  const { title, subTitle, _rawContent: content } = section
  const [applicantAnnualIncome, setApplicantAnnualIncome] =
    useState<number>(80000)
  const [coApplicantAnnualIncome, setCoApplicantAnnualIncome] =
    useState<number>(0)
  const [monthlyDebt, setMonthlyDebt] = useState<number>(0)
  const [interestRate, setInterestRate] = useState<number>(5)
  const [annualPropTax, setAnnualPropTax] = useState<number>(2500)
  const [annualHeaingCost, setAnnualHeaingCost] = useState<number>(0)
  const [monthlyCondoFee, setMonthlyCondoFee] = useState<number>(0)
  const [amortization, setAmortization] = useState<number>(25)

  const [maxMonthlyMortgagePayment, setMaxMonthlyMortgagePayment] =
    useState<number>(0)
  const [maxHomePrice, setMaxHomePrice] = useState<number>(0)

  const formatWithCommas = (value: number) => {
    return Number(value).toLocaleString()
  }

  const handleApplicantAnnualIncome = (e: any) => {
    setApplicantAnnualIncome(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handleCoApplicantAnnualIncome = (e: any) => {
    setCoApplicantAnnualIncome(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handlemonthlyDebt = (e: any) => {
    setMonthlyDebt(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handleAnnualHeaingCost = (e: any) => {
    setAnnualHeaingCost(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handleAmortization = (e: any) => {
    setAmortization(e.target.value)
  }

  const handleInterestRate = (e: any) => {
    setInterestRate(e.target.value.replace("%", ""))
  }

  const handleAnnualPropTax = (e: any) => {
    setAnnualPropTax(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handleMonthlyCondoFee = (e: any) => {
    setMonthlyCondoFee(parseFloat(e.target.value.replace(/\D/g, "")))
  }

  const handleKeyDown = (identifier: string) => (e: any) => {

      if (e.key === 'Tab') return;
      
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const input = e.target;
        const value = input.value;
        if (value.startsWith('$')) {
          e.preventDefault();
          input.value = value.slice(0, -1);
          if (identifier === 'applicantAnnualIncome') {
              setApplicantAnnualIncome(parseFloat(input.value.replace(/\D/g, '')) || 0);
          } else if (identifier === 'co_applicantAnnualIncome') {
              setCoApplicantAnnualIncome(parseFloat(input.value.replace('$', '')) || 0);
          } else if (identifier === 'annualPropTax') {
              setAnnualPropTax(parseFloat(input.value.replace('$', '')) || 0);
          } else if (identifier === 'monthlyFee') {
              setMonthlyCondoFee(parseFloat(input.value.replace('$', '')) || 0);
          }
        }
        if (value.endsWith('%')) {
          e.preventDefault();
          input.value = value.slice(0, -1);
          if (identifier === 'rateInterest') {
              setInterestRate(parseFloat(input.value.replace('%', '')) || 0);
          }
        }
      } else {
        const isNumeric = /^\d*\.?\d*$/.test(e.key);
        const isSpecialKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', '.', 'Tab'].includes(e.key);
        if (!isNumeric && !isSpecialKey) {
          e.preventDefault();
        }
      }
  };


  useEffect(() => {
    const gdsMax = 38.9
    const tdsMax = 44
    const annualIncome = applicantAnnualIncome + coApplicantAnnualIncome
    const propertyTaxes = annualPropTax
    const heatingExp = annualHeaingCost
    const otherDebtPayments = monthlyDebt
    const condoFee = monthlyCondoFee

    setMaxMonthlyMortgagePayment(
      calculateAffordableMortgage(
        gdsMax,
        tdsMax,
        annualIncome,
        propertyTaxes,
        heatingExp,
        condoFee,
        otherDebtPayments
      )
    )
  }, [
    applicantAnnualIncome,
    coApplicantAnnualIncome,
    annualPropTax,
    annualHeaingCost,
    monthlyDebt,
    monthlyCondoFee,
  ])

  useEffect(() => {
    setMaxHomePrice(
      calculateMaximumMortgage(
        maxMonthlyMortgagePayment,
        interestRate,
        amortization
      )
    )
  }, [maxMonthlyMortgagePayment, interestRate, amortization])

  function calculateAffordableMortgage(
    gdsMax: number, // Maximum GDS ratio (e.g., 40%)
    tdsMax: number, // Maximum TDS ratio (e.g., 40%)
    annualIncome: number, // Annual incomed and loans
    propertyTaxes: number, // Annual property taxes
    heatingExp: number,
    condoFee: number,
    otherDebtPayments: number
  ) {
    const monthlyIncome = annualIncome / 12
    const monthlyPropertyTaxes = propertyTaxes / 12
    const monthlyHeatingExp = heatingExp
    const maxHousingExpenseGDS = (monthlyIncome * gdsMax) / 100
    const maxTotalDebtExpenseTDS =
      (monthlyIncome * tdsMax) / 100 - otherDebtPayments
    const maxMortgagePayment =
      Math.min(maxHousingExpenseGDS, maxTotalDebtExpenseTDS) -
      (monthlyPropertyTaxes + monthlyHeatingExp + condoFee * 0.5)

    return maxMortgagePayment
  }

  function calculateMaximumMortgage(
    maxMonthlyPayment: number,
    annualInterestRate: number,
    amortizationYears: number
  ) {
    const annualInterestRateDecimal = annualInterestRate / 100
    const monthlyInterestRate =
      Math.pow(1 + annualInterestRateDecimal / 2, 1 / 6) - 1
    const totalPayments = amortizationYears * 12
    const maximumMortgage =
      maxMonthlyPayment *
      ((1 - Math.pow(1 + monthlyInterestRate, -totalPayments)) /
        monthlyInterestRate)
    return maximumMortgage
  }

  return (
    <Container
      className="flex flex-col w-full gap-10 mt-10 md:mt-14"
      fullWidthOnMobile={false}
      constrainedToBreakPoint
    >
      <Box className="w-full ">
        <Heading as="h1" className="text-blue-500 mb-8 text-left">
          {title}
        </Heading>
        <Text
          variant="large"
          className="text-blue-500 !text-base mb-8 text-left"
        >
          {subTitle}
        </Text>
      </Box>
    <section className="mortage_section">
      <div className="p-5">
        <div className="grid grid-cols-3 gap-4 grid-flow-row container xl mx-auto">
          <div className="p-5 text-left bg-white row-start-1 row-end-4 border-solid border-2 border-gray-300 sm:rounded-lg">
            <form>
              <h4 className="mb-4 text-sm font-bold dark:text-white blueColor">
                Annual Incomes
              </h4>
              <div className="grid gap-5 mb-6 md:grid-cols-2">
                <div className="relative">
                  <input
                    type="text"
                    id="aaplicant"
                    className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder=" "
                    value={"$" + formatWithCommas(applicantAnnualIncome)}
                    onChange={handleApplicantAnnualIncome}
                    onKeyDown={handleKeyDown("applicantAnnualIncome")}
                    inputMode="numeric"
                  />
                  <label
                    htmlFor="fapplicant"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Applicant*
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    id="co_applicant"
                    className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder=""
                    value={"$" + formatWithCommas(coApplicantAnnualIncome)}
                    onChange={handleCoApplicantAnnualIncome}
                    onKeyDown={handleKeyDown("co_applicantAnnualIncome")}
                    inputMode="numeric"
                  />
                  <label
                    htmlFor="co_applicant"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Co-Applicant
                  </label>
                </div>
              </div>

              <h4 className="mb-4 text-sm font-bold dark:text-white blueColor">
                Monthly Expenses
              </h4>
              <div className="mb-6 relative">
                <input
                  type="text"
                  id="monthly_debt"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder=" "
                  value={"$" + formatWithCommas(monthlyDebt)}
                  onChange={handlemonthlyDebt}
                  onKeyDown={handleKeyDown("monthlyDebt")}
                  inputMode="numeric"
                />
                <label
                  htmlFor="monthly_debt"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Monthly Debt Payments
                </label>
              </div>
              <div className="mb-6 relative">
                <input
                  type="text"
                  id="add_spending"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder=" "
                  value={"$" + formatWithCommas(annualHeaingCost)}
                  onChange={handleAnnualHeaingCost}
                  onKeyDown={handleKeyDown("additionalSpending")}
                  inputMode="numeric"
                />
                <label
                  htmlFor="add_spending"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Monthly Heating Cost
                </label>
              </div>

              <h4 className="mb-4 text-sm font-bold dark:text-white blueColor">
                Mortgage Info
              </h4>
              <div className="mb-6 relative">
                <select
                  id="amortization_period"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  value={amortization}
                  onChange={handleAmortization}
                >
                  <option value={5}>5 Years</option>
                  <option value={10}>10 Years</option>
                  <option value={15}>15 Years</option>
                  <option value={20}>20 Years</option>
                  <option value={25}>25 Years</option>
                  <option value={30}>30 Years</option>
                </select>
                <label
                  htmlFor="amortization_period"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Amortization
                </label>
              </div>
              <div className="mb-6 relative">
                <input
                  type="text"
                  id="interest_rate"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder=" "
                  value={interestRate + "%"}
                  onChange={handleInterestRate}
                  onKeyDown={handleKeyDown("rateInterest")}
                  inputMode="numeric"
                />
                <label
                  htmlFor="interest_rate"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Interest Rate*
                </label>
              </div>

              <h4 className="mb-4 text-sm font-bold dark:text-white blueColor">
                Additional Housing Costs
              </h4>
              <div className="mb-6 relative">
                <input
                  type="text"
                  id="annual_propTax"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder=" "
                  value={"$" + formatWithCommas(annualPropTax)}
                  onChange={handleAnnualPropTax}
                  onKeyDown={handleKeyDown("annualPropTax")}
                  inputMode="numeric"
                />
                <label
                  htmlFor="annual_propTax"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Annual Property Tax
                </label>
              </div>
              <div className="mb-6 relative">
                <input
                  type="text"
                  id="monthly_condo_fees"
                  className="p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder=" "
                  value={"$" + formatWithCommas(monthlyCondoFee)}
                  onChange={handleMonthlyCondoFee}
                  onKeyDown={handleKeyDown("monthlyFee")}
                  inputMode="numeric"
                />
                <label
                  htmlFor="monthly_condo_fees"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Monthly Condo Fees
                </label>
              </div>
            </form>
          </div>
          <div className="p-5 text-left bg-white col-start-2 col-end-4">
            <h1 className="mb-12 text-4xl font-bold dark:text-white blueColor">
              What Can I Afford?
            </h1>
            <h5 className="mb-1 text-xl font-bold dark:text-white blueColor">
              Your affordability scenario
            </h5>
            <p className="mb-6 text-sm font-light">
              Based on the information you provided, a house at this price
              should fit within your budget.
            </p>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 custom_grid">
                <div className="text-sm text-center">
                  <h5 className="mb-1 text-6xl dark:text-white blueColor">
                    ${formatWithCommas(Math.round(maxHomePrice))}
                  </h5>
                  <p className="mb-6 text-sm font-light">Max. Home Price</p>
                </div>
              </div>
              <div className="grid md:grid-cols-1">
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="text-sm text-center">
                    <h5 className="mb-1 text-6xl dark:text-white blueColor">
                      $
                      {formatWithCommas(
                        Math.round(
                          maxMonthlyMortgagePayment + annualPropTax / 12
                        )
                      )}
                    </h5>
                    <p className="mb-6 text-sm font-light">
                      Max Monthly Mortgage Payment
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Monthly Mortgage</div>
                <div className="text-sm font-bold text-right">
                  ${formatWithCommas(Math.round(maxMonthlyMortgagePayment))}
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Interest Rate</div>
                <div className="text-sm font-bold text-right">
                  {interestRate}%
                </div>
              </div>
            </div>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Loan Amount</div>
                <div className="text-sm font-bold text-right">
                  ${formatWithCommas(Math.round(maxHomePrice))}
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Mortgage Term</div>
                <div className="text-sm font-bold text-right">5 Years</div>
              </div>
            </div>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Payment Frequency</div>
                <div className="text-sm font-bold text-right">Monthly</div>
              </div>
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>Amortization Period</div>
                <div className="text-sm font-bold text-right">
                  {amortization} Years
                </div>
              </div>
            </div>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                <div>No. of Payments</div>
                <div className="text-sm font-bold text-right">
                  {amortization * 12}
                </div>
              </div>
            </div>

            <h4 className="mb-2 text-xl font-bold blueColor">
              Mortgage Default Insurance
            </h4>
            <div className="grid gap-12 mb-6 md:grid-cols-2 border-b pb-3">
              <div>Max Mortgage + Mortgage Default Insurance</div>
              <div className="text-sm font-bold text-right">
                ${formatWithCommas(Math.round(maxHomePrice))}
              </div>
            </div>
            <div className="grid gap-12 mb-6 md:grid-cols-2 border-b pb-3">
              <div>Max Mortgage Payment after Mortgage Default Insurance</div>
              <div className="text-sm font-bold text-right">
                ${formatWithCommas(Math.round(maxMonthlyMortgagePayment))}
              </div>
            </div>

            <h4 className="mb-2 text-xl font-bold dark:text-white blueColor">
              Estimated Monthly Expenses
            </h4>
            <div className="grid gap-12 mb-6 md:grid-cols-2">
              <div className="grid gap-5 md:grid-cols-2 pb-3 custom_grid">
                <div className="text-sm font-bold">
                  <h5 className="mb-1 text-6xl blueColor">
                    $
                    {formatWithCommas(
                      Math.round(maxMonthlyMortgagePayment) +
                        Math.round(monthlyDebt + annualHeaingCost) +
                        Math.round(annualPropTax / 12 + monthlyCondoFee)
                    )}
                  </h5>
                  <p className="mb-6 text-sm font-light">
                    Total Estimated Monthly Expenses
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-1">
                <div className="grid gap-5 md:grid-cols-2 border-b pb-3 mb-3">
                  <div>
                    <span
                      className="colored_circle"
                      style={{ backgroundColor: "#008254" }}
                    ></span>{" "}
                    Monthly Mortgage Payment
                  </div>
                  <div className="text-sm font-bold text-right">
                    ${formatWithCommas(Math.round(maxMonthlyMortgagePayment))}
                  </div>
                </div>
                <div className="grid gap-5 md:grid-cols-2 border-b pb-3 mb-3">
                  <div>
                    <span
                      className="colored_circle"
                      style={{ backgroundColor: "#40ad87" }}
                    ></span>{" "}
                    Monthly Expenses
                  </div>
                  <div className="text-sm font-bold text-right">
                    $
                    {formatWithCommas(
                      Math.round(monthlyDebt + annualHeaingCost)
                    )}
                  </div>
                </div>
                <div className="grid gap-5 md:grid-cols-2 border-b pb-3">
                  <div>
                    <span
                      className="colored_circle"
                      style={{ backgroundColor: "#efa92e" }}
                    ></span>{" "}
                    Additional Housing Expenses
                  </div>
                  <div className="text-sm font-bold text-right">
                    $
                    {formatWithCommas(
                      Math.round(annualPropTax / 12 + monthlyCondoFee)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Box className="w-full ">
        <div
          className="ratehub-calc small"
          data-widget="calc-payment"
          data-lang="en"
        ></div>
        <Box className="mt-4">
          <PortableText components={serializers} value={content} />
        </Box>
      </Box>
    </Container>
  )
}

export default SectionAffordabilityCalculator
